import React, { useMemo, useState } from "react";
import {
  FaArrowRight,
  FaCheck,
  FaGift,
  FaHandshake,
  FaPercentage,
  FaRegHandPeace,
  FaUsers,
} from "react-icons/fa";
import { createParrainage } from "../../services/parrainageServices";
import Reveal from "../../components/ui/Reveal";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const INITIAL_FORM = {
  nom_parrain: "",
  prenom_parrain: "",
  club_parrain: "",
  email_parrain: "",
  telephone_parrain: "",
  club_parraine: "",
  nom_contact_parraine: "",
  prenom_contact_parraine: "",
  email_parraine: "",
  telephone_parraine: "",
};

const BENEFITS = [
  {
    icon: <FaPercentage />,
    title: "5% de réduction",
    text: "Le club parrain bénéficie de 5% de remise sur son prochain abonnement annuel.",
  },
  {
    icon: <FaGift />,
    title: "5% pour le club parrainé",
    text: "Le club parrainé profite également de 5% de remise sur sa première facture annuelle.",
  },
  {
    icon: <FaHandshake />,
    title: "Jusqu’à 10%",
    text: "Si plusieurs clubs sont parrainés sur une même saison, la remise peut atteindre 10%.",
  },
];

const STEPS = [
  "Vous recommandez MonAppliClub à un autre club.",
  "Le club parrainé prend contact ou souscrit à l’une des offres éligibles.",
  "Les deux clubs bénéficient de leur remise selon les conditions du programme.",
];

function InputField({
  id,
  name,
  label,
  type = "text",
  value,
  onChange,
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200"
      >
        {label}
      </label>

      <input
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className={cx(
          "h-12 w-full rounded-2xl border px-4 text-sm outline-none transition",
          "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400",
          "focus:border-[#0868f2] focus:ring-4 focus:ring-blue-100",
          "dark:border-white/10 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500",
          "dark:focus:border-blue-400 dark:focus:ring-blue-500/10",
        )}
      />
    </div>
  );
}

function BenefitCard({ item }) {
  return (
    <div className="rounded-[22px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-[0_18px_45px_-30px_rgba(15,23,42,0.25)] dark:border-white/10 dark:bg-slate-900">
      <div className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
        {item.icon}
      </div>

      <h3 className="mt-4 text-lg font-semibold tracking-tight text-slate-950 dark:text-white">
        {item.title}
      </h3>

      <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
        {item.text}
      </p>
    </div>
  );
}

function StepItem({ index, text }) {
  return (
    <div className="flex items-start gap-4">
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#0868f2] text-xs font-bold text-white">
        {index}
      </div>

      <p className="pt-1 text-sm leading-7 text-slate-600 dark:text-slate-300">
        {text}
      </p>
    </div>
  );
}

export default function Parrainage() {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState({
    type: "",
    message: "",
  });
  const [formData, setFormData] = useState(INITIAL_FORM);

  const summaryText = useMemo(() => {
    return "Parrainez un autre club et faites bénéficier les deux structures d’une remise simple et avantageuse.";
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setSubmitState({ type: "", message: "" });

    try {
      const response = await createParrainage(formData);

      if (response) {
        setSubmitState({
          type: "success",
          message:
            "Votre demande de parrainage a bien été envoyée. Je reviendrai vers vous rapidement.",
        });

        resetForm();
        setShowForm(false);
      }
    } catch (error) {
      setSubmitState({
        type: "error",
        message:
          "Une erreur est survenue lors de l’envoi du parrainage. Merci de réessayer.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="parrainage"
      className="scroll-mt-24 bg-white py-16 dark:bg-slate-950 sm:py-20"
    >
      <div className="mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="grid gap-10 border-b border-slate-200 pb-10 dark:border-white/10 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0868f2] dark:text-blue-300">
                Programme de parrainage
              </p>

              <h2 className="mt-5 max-w-4xl text-4xl font-semibold leading-[1.08] tracking-[-0.04em] text-slate-950 dark:text-white sm:text-5xl">
                Recommandez MonAppliClub,
                <span className="block text-[#0868f2] dark:text-blue-300">
                  et profitez d’avantages partagés.
                </span>
              </h2>
            </div>

            <div className="max-w-xl lg:justify-self-end">
              <p className="text-base leading-8 text-slate-600 dark:text-slate-300">
                {summaryText}
              </p>

              <p className="mt-3 text-base leading-8 text-slate-600 dark:text-slate-300">
                Une démarche simple, utile et gagnante pour le club parrain
                comme pour le club parrainé.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-10 xl:grid-cols-[1.02fr_0.98fr]">
          <div>
            <Reveal>
              <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-slate-950 shadow-[0_24px_80px_-45px_rgba(15,23,42,0.45)] dark:border-white/10">
                <div className="relative aspect-[16/11] overflow-hidden">
                  <img
                    src="/images/parrainage_02.png"
                    alt="Programme de parrainage MonAppliClub"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />

                  <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/60 to-slate-950/20" />

                  <div className="absolute left-6 top-6">
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white backdrop-blur">
                      <FaRegHandPeace className="text-[#ff7900]" />
                      Parrainage clubs
                    </span>
                  </div>

                  <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                    <h3 className="max-w-xl text-3xl font-semibold leading-tight tracking-[-0.03em] text-white sm:text-4xl">
                      Une recommandation peut devenir un vrai avantage pour
                      deux clubs.
                    </h3>

                    <p className="mt-4 max-w-2xl text-sm leading-7 text-white/75 sm:text-base">
                      Faites découvrir MonAppliClub à une autre structure et
                      bénéficiez ensemble d’une remise sur l’abonnement annuel.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {BENEFITS.map((item) => (
                <Reveal key={item.title}>
                  <BenefitCard item={item} />
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <Reveal>
              <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-6 dark:border-white/10 dark:bg-slate-900/50 sm:p-7">
                <div className="flex items-center gap-3">
                  <div className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-[#0868f2] dark:bg-blue-500/10 dark:text-blue-300">
                    <FaUsers />
                  </div>

                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#ff7900]">
                      Comment ça fonctionne
                    </p>
                    <h3 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">
                      Un fonctionnement simple
                    </h3>
                  </div>
                </div>

                <div className="mt-6 space-y-5">
                  {STEPS.map((step, index) => (
                    <StepItem key={step} index={index + 1} text={step} />
                  ))}
                </div>

                <div className="mt-8 rounded-[22px] border border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-slate-950">
                  <p className="text-sm font-semibold text-slate-950 dark:text-white">
                    Conditions résumées
                  </p>

                  <div className="mt-4 space-y-3">
                    <div className="flex items-start gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
                        <FaCheck className="text-[8px]" />
                      </span>
                      <span>
                        Remise valable sur les offres <strong>Complète</strong>{" "}
                        ou <strong>Light</strong>.
                      </span>
                    </div>

                    <div className="flex items-start gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
                        <FaCheck className="text-[8px]" />
                      </span>
                      <span>
                        Le club parrain et le club parrainé profitent chacun de
                        leur remise selon les conditions du programme.
                      </span>
                    </div>

                    <div className="flex items-start gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
                        <FaCheck className="text-[8px]" />
                      </span>
                      <span>
                        Plusieurs parrainages sur une saison peuvent ouvrir à
                        une remise plus avantageuse.
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm((prev) => !prev);
                      setSubmitState({ type: "", message: "" });
                    }}
                    className={cx(
                      "group inline-flex h-12 items-center justify-center gap-3 rounded-full px-6 text-sm font-semibold transition",
                      showForm
                        ? "border border-slate-300 bg-white text-slate-900 hover:bg-slate-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:hover:bg-slate-800"
                        : "bg-[#0868f2] text-white hover:bg-[#0648b8]",
                    )}
                  >
                    {showForm ? "Fermer le formulaire" : "Parrainer un club"}

                    <span
                      className={cx(
                        "grid h-7 w-7 place-items-center rounded-full transition",
                        showForm
                          ? "bg-slate-100 text-[#0868f2] dark:bg-slate-800 dark:text-blue-300"
                          : "bg-[#ff7900] text-white",
                      )}
                    >
                      <FaArrowRight className="text-[9px] transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </button>

                  <a
                    href="#contact"
                    className="inline-flex h-12 items-center justify-center rounded-full border border-slate-300 px-6 text-sm font-semibold text-slate-800 transition hover:border-[#0868f2] hover:text-[#0868f2] dark:border-white/10 dark:text-white dark:hover:border-blue-300 dark:hover:text-blue-300"
                  >
                    Poser une question
                  </a>
                </div>

                {submitState.message ? (
                  <div
                    className={cx(
                      "mt-5 rounded-2xl border px-4 py-3 text-sm",
                      submitState.type === "success"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                        : "border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300",
                    )}
                  >
                    {submitState.message}
                  </div>
                ) : null}
              </div>
            </Reveal>
          </div>
        </div>

        {showForm ? (
          <Reveal className="mt-10">
            <div className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_28px_90px_-50px_rgba(15,23,42,0.35)] dark:border-white/10 dark:bg-slate-900">
              <div className="border-b border-slate-200 px-6 py-6 dark:border-white/10 sm:px-8">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0868f2] dark:text-blue-300">
                  Formulaire de parrainage
                </p>

                <h3 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white sm:text-3xl">
                  Renseignez les deux clubs
                </h3>

                <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
                  Complétez les informations du club parrain et du club
                  parrainé. Je reviendrai ensuite vers vous pour valider le
                  parrainage.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="p-6 sm:p-8">
                <div className="grid gap-8 lg:grid-cols-2">
                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-slate-950/60 sm:p-6">
                    <div className="mb-5 flex items-center gap-3">
                      <div className="grid h-10 w-10 place-items-center rounded-2xl bg-blue-50 text-[#0868f2] dark:bg-blue-500/10 dark:text-blue-300">
                        <FaHandshake />
                      </div>

                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#ff7900]">
                          Club parrain
                        </p>
                        <h4 className="text-lg font-semibold text-slate-950 dark:text-white">
                          Vos informations
                        </h4>
                      </div>
                    </div>

                    <div className="grid gap-4">
                      <InputField
                        id="club_parrain"
                        name="club_parrain"
                        label="Nom du club"
                        value={formData.club_parrain}
                        onChange={handleInputChange}
                        required
                        placeholder="Ex. Judo Club de Bordeaux"
                      />

                      <div className="grid gap-4 sm:grid-cols-2">
                        <InputField
                          id="nom_parrain"
                          name="nom_parrain"
                          label="Nom"
                          value={formData.nom_parrain}
                          onChange={handleInputChange}
                          required
                        />

                        <InputField
                          id="prenom_parrain"
                          name="prenom_parrain"
                          label="Prénom"
                          value={formData.prenom_parrain}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <InputField
                        id="email_parrain"
                        name="email_parrain"
                        label="Email"
                        type="email"
                        value={formData.email_parrain}
                        onChange={handleInputChange}
                        required
                      />

                      <InputField
                        id="telephone_parrain"
                        name="telephone_parrain"
                        label="Téléphone"
                        value={formData.telephone_parrain}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-slate-950/60 sm:p-6">
                    <div className="mb-5 flex items-center gap-3">
                      <div className="grid h-10 w-10 place-items-center rounded-2xl bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
                        <FaUsers />
                      </div>

                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#0868f2] dark:text-blue-300">
                          Club parrainé
                        </p>
                        <h4 className="text-lg font-semibold text-slate-950 dark:text-white">
                          Club recommandé
                        </h4>
                      </div>
                    </div>

                    <div className="grid gap-4">
                      <InputField
                        id="club_parraine"
                        name="club_parraine"
                        label="Nom du club"
                        value={formData.club_parraine}
                        onChange={handleInputChange}
                        required
                        placeholder="Ex. Club sportif recommandé"
                      />

                      <div className="grid gap-4 sm:grid-cols-2">
                        <InputField
                          id="nom_contact_parraine"
                          name="nom_contact_parraine"
                          label="Nom du contact"
                          value={formData.nom_contact_parraine}
                          onChange={handleInputChange}
                        />

                        <InputField
                          id="prenom_contact_parraine"
                          name="prenom_contact_parraine"
                          label="Prénom du contact"
                          value={formData.prenom_contact_parraine}
                          onChange={handleInputChange}
                        />
                      </div>

                      <InputField
                        id="email_parraine"
                        name="email_parraine"
                        label="Email du club parrainé"
                        type="email"
                        value={formData.email_parraine}
                        onChange={handleInputChange}
                      />

                      <InputField
                        id="telephone_parraine"
                        name="telephone_parraine"
                        label="Téléphone du club parrainé"
                        value={formData.telephone_parraine}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-col gap-4 border-t border-slate-200 pt-6 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
                  <p className="max-w-2xl text-sm leading-7 text-slate-500 dark:text-slate-400">
                    En envoyant ce formulaire, vous me transmettez les
                    informations nécessaires pour prendre contact avec les
                    structures concernées.
                  </p>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={cx(
                      "group inline-flex h-12 shrink-0 items-center justify-center gap-3 rounded-full px-6 text-sm font-semibold transition",
                      isSubmitting
                        ? "cursor-not-allowed bg-slate-300 text-white dark:bg-slate-700"
                        : "bg-[#0868f2] text-white hover:bg-[#0648b8]",
                    )}
                  >
                    {isSubmitting ? "Envoi en cours..." : "Soumettre le parrainage"}

                    <span className="grid h-7 w-7 place-items-center rounded-full bg-[#ff7900] text-white">
                      <FaArrowRight className="text-[9px]" />
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </Reveal>
        ) : null}
      </div>
    </section>
  );
}